import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useEffect, useState } from "react";
import { FieldError, useFieldArray, useForm } from "react-hook-form";
import InventoryProductSearchComponent from "../OrderPurchaseComponent/OrderPurchaseAddComponent/InventoryProductSearchComponent";
import {
  IAddInventoryPostBody,
  IInventoryPurchaseOrderProductSearchResponseData,
} from "@/api_framework/api_modals/Inventory";
import { toast } from "react-hot-toast";
import Input from "@/app/components/ui/Inputs/Input";
import useFormValidations from "@/app/hooks/useFormValidations";
import { TrashIcon } from "@heroicons/react/24/outline";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { format } from "date-fns";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { useGetProductCostAPI } from "@/app/hooks/api_hooks/Inventory/PO/useGetProductCostAPI";
import { useAddInventoryOnboardAPI } from "@/app/hooks/api_hooks/Inventory/PO/useAddInventoryOnboardAPI";
import { useNavigate } from "react-router";

export interface IInventoryPurchaseOrderDraftProductInfo {
  variant_name?: string;
  variant_id: string;
  in_hand_quantity: number;
  price_per_package: number | null;
  lot_number: string;
  manufacturer_name: string;
  mrp: number;
  expiry_date: string;
}

export interface IAddInventoryProps {
  type: string;
  clinic_tams: string;
  clinic_tams_id: string;
  reason: string;
  product_info: IInventoryPurchaseOrderDraftProductInfo[];
}

const types = [
  {
    id: "1",
    title: "TAMS",
  },
  {
    id: "2",
    title: "CLINIC",
  },
];

const AddInventory = () => {
  const { execute: fetchClinicList, clinicList } = useClinicTamsFilterAPI();
  const clinicsOrgItem = useSelectMenuReducer(clinicList, "display_name", "id");
  const { forNumericWithDecimalAndNotStartFromZero, forOnlyNumberWithoutZero } = useFormValidations();
  const { getCurrentDate } = useUtils();
  const stringConstants = useStringConstants();
  const { execute: fetchProductCost } = useGetProductCostAPI();
  const { execute: addInventory } = useAddInventoryOnboardAPI();
  const navigate = useNavigate();
  const [selectedProducts, setSelectedProducts] = useState<{ [key: string]: string | number }>({});

  const formHook = useForm<IAddInventoryProps>({
    mode: "onChange",
    defaultValues: { type: "", clinic_tams: "", product_info: [] },
  });

  const POProductsHook = useFieldArray<IAddInventoryProps>({
    control: formHook.control,
    name: "product_info",
  });

  useEffect(() => {
    const selectedType = formHook.watch("type");
    if (selectedType) {
      if (selectedType === "TAMS") {
        fetchClinicList("", "TAMS");
      } else if (selectedType === "CLINIC") {
        fetchClinicList("", "CLINIC");
      }
    }
  }, [formHook.watch("type")]);

  const handleAddProductItem = async (item: IInventoryPurchaseOrderProductSearchResponseData) => {
    if (!item?.vetic_variant_id) return;
    const isPresent = POProductsHook?.fields?.some((f) => f?.variant_id === item?.vetic_variant_id);
    if (isPresent) {
      toast.error("Product already added.");
      return;
    }
    try {
      const cost = await fetchProductCost(item?.vetic_variant_id, formHook.watch("clinic_tams_id"));
      POProductsHook.append({
        variant_name: item?.variant_name,
        variant_id: item?.vetic_variant_id,
        in_hand_quantity: null,
        price_per_package: cost || null,
        expiry_date: "",
        lot_number: "",
        manufacturer_name: "",
        mrp: null,
      });
      setSelectedProducts((prev) => ({
        ...prev,
        [item?.vetic_variant_id]: cost || null,
      }));
    } catch (error) {
      console.error("Error fetching product cost:", error);
    }
  };

  const onSubmit = async (data: IAddInventoryProps) => {
    if (!data?.product_info || data?.product_info?.length === 0) {
      toast.error("Please select at least one product.");
      return;
    }
    const constructedBody: IAddInventoryPostBody = {
      clinic_details: {
        clinic_id: data?.clinic_tams_id,
        clinic_name: data?.clinic_tams,
      },
      reason: data?.reason,
      grn_request: Array.isArray(data?.product_info)
        ? data?.product_info?.map((product) => ({
            variant_id: product?.variant_id,
            variant_name: product?.variant_name,
            lot_number: product?.lot_number,
            manufacturer_name: product?.manufacturer_name,
            mrp: Number(product?.mrp),
            expiry_date: product?.expiry_date,
            in_hand_quantity: Number(product?.in_hand_quantity),
            price_per_package: Number(product?.price_per_package),
          }))
        : [],
    };
    addInventory(constructedBody).then(({ status }) => {
      if (status) {
        navigate(`/app/inventory/inventory-snapshot`);
      }
    });
  };

  const isInputDisabled = () => {
    const clinicId = formHook.getValues("clinic_tams") || "";
    const type = formHook.getValues("type") || "";
    return clinicId === "" || type === "";
  };

  return (
    <div className="bg-white px-4 p-4 rounded-md">
      <h2 className="flex justify-start items-center gap-x-3 border-gray-200 pb-3 border-b font-semibold text-sm">
        Add Inventory
      </h2>
      <form className="mt-5 pb-24" onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="flex sm:flex-row flex-col sm:items-end gap-3">
          <div className="sm:w-auto w-full mt-2">
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: "Type is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("type", item?.title);
                }
              }}
              label="Type"
              fieldError={(formHook?.formState?.errors?.type as FieldError) ?? null}
              register={formHook.register("type", { required: true })}
              selectItems={types}
              placeholder="Select Type"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={types?.filter((oc) => oc.title === formHook.watch("type"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
            />
          </div>
          <div className="sm:w-1/3 lg:w-1/6 w-full mt-2">
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: "Clinic/Tams is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinic_tams", item?.title);
                  formHook.setValue("clinic_tams_id", item?.id);
                }
              }}
              label="Clinic/Tams"
              fieldError={(formHook?.formState?.errors?.clinic_tams as FieldError) ?? null}
              register={formHook.register("clinic_tams", { required: true })}
              selectItems={clinicsOrgItem}
              placeholder="Select Clinc/Tams"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={clinicsOrgItem?.filter((oc) => oc.title === formHook.watch("clinic_tams"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
            />
          </div>
          <div className="shadow-sm sm:flex-1 lg:flex-none lg:w-1/4 w-full">
            <div>
              <InventoryProductSearchComponent
                variant_category_id=""
                type={formHook.watch("type")}
                onSelectItem={handleAddProductItem}
                containerClassName="shadow-none"
                itemActiveClassName="bg-orange-500 text-white"
                inputContainerClassName="w-full"
                flexibleAdd={false}
                isInputEnabled={isInputDisabled()}
              />
            </div>
          </div>
        </div>
        <div>
          {POProductsHook?.fields && POProductsHook?.fields?.length > 0 && (
            <div className="max-w-full border border-gray-200 rounded-lg mt-8 overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
              <table className="w-full min-w-max border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-left text-[10px] text-xs 2xl:text-sm font-medium sticky top-0">
                    <th className="z-20 sticky top-0 left-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2 bg-gray-200">
                      S.No
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">
                      Product Name
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">
                      In Hand Qty
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">
                      Item Cost
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">
                      Lot Number
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">
                      Manufacturer Name
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">
                      MRP
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">
                      Expiry Date
                    </th>
                    <th className="sticky top-0 font-medium text-black text-[10px] text-xs 2xl:text-sm px-4 py-2">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[10px] text-xs 2xl:text-sm">
                  {POProductsHook?.fields?.map((item, index) => (
                    <tr key={item.id} className="odd:bg-white even:bg-gray-50">
                      <td className="z-10 sticky left-0 bg-gray-100 px-4 py-2">{index + 1}.</td>
                      <td className="px-4 py-5 whitespace-nowrap">{item?.variant_name}</td>
                      <td className="px-4 py-5">
                        <div>
                          <Input
                            placeholder="Add Qty"
                            errorMessages={[forOnlyNumberWithoutZero?.errors, { message: "", type: "required" }]}
                            fieldError={
                              formHook?.formState?.errors?.product_info
                                ? formHook?.formState?.errors?.product_info[index]
                                  ? formHook?.formState?.errors?.product_info[index]?.in_hand_quantity
                                  : null
                                : null
                            }
                            className="shadow-none w-32"
                            register={formHook.register(`product_info.${index}.in_hand_quantity`, {
                              required: true,
                              ...forOnlyNumberWithoutZero?.validations,
                            })}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div>
                          <Input
                            placeholder="Add Cost Price"
                            errorMessages={[{ message: "", type: "required" }]}
                            fieldError={formHook?.formState?.errors?.product_info?.[index]?.price_per_package || null}
                            className="w-full"
                            register={formHook.register(`product_info.${index}.price_per_package`, {
                              required: true,
                            })}
                            value={selectedProducts[item?.variant_id] ?? item?.price_per_package}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div>
                          <Input
                            placeholder="Add Lot Number"
                            errorMessages={[{ message: "", type: "required" }]}
                            fieldError={
                              formHook?.formState?.errors?.product_info
                                ? formHook?.formState?.errors?.product_info[index]
                                  ? formHook?.formState?.errors?.product_info[index]?.lot_number
                                  : null
                                : null
                            }
                            className="w-full"
                            register={formHook.register(`product_info.${index}.lot_number`, {
                              required: false,
                            })}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div>
                          <Input
                            placeholder="Add Manufacturer Name"
                            errorMessages={[{ message: "", type: "required" }]}
                            fieldError={
                              formHook?.formState?.errors?.product_info
                                ? formHook?.formState?.errors?.product_info[index]
                                  ? formHook?.formState?.errors?.product_info[index]?.manufacturer_name
                                  : null
                                : null
                            }
                            className="w-full"
                            register={formHook.register(`product_info.${index}.manufacturer_name`, {
                              required: false,
                            })}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div>
                          <Input
                            placeholder="Add MRP"
                            errorMessages={[
                              forNumericWithDecimalAndNotStartFromZero?.errors,
                              { message: "", type: "required" },
                            ]}
                            fieldError={
                              formHook?.formState?.errors?.product_info
                                ? formHook?.formState?.errors?.product_info[index]
                                  ? formHook?.formState?.errors?.product_info[index]?.mrp
                                  : null
                                : null
                            }
                            className="w-full"
                            register={formHook.register(`product_info.${index}.mrp`, {
                              required: true,
                              ...forNumericWithDecimalAndNotStartFromZero?.validations,
                            })}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div>
                          <PWLCalendarComponent
                            disablePastMonthSelection
                            errorMessages={[{ message: "", type: "required" }]}
                            register={formHook.register(`product_info.${index}.expiry_date`, {
                              required: false,
                            })}
                            disableDatesBeforeDate={getCurrentDate()}
                            fieldError={
                              formHook?.formState?.errors?.product_info
                                ? formHook?.formState?.errors?.product_info?.[index]
                                  ? formHook?.formState?.errors?.product_info?.[index]?.expiry_date
                                    ? formHook?.formState?.errors?.product_info?.[index]?.expiry_date
                                    : null
                                  : null
                                : null
                            }
                            allowPastDaySelection={false}
                            showFutureYears={true}
                            onSelectDate={(date: Date) => {
                              const formattedDate = format(date, stringConstants?.fieldDOBFormat);
                              formHook.setValue(`product_info.${index}.expiry_date`, formattedDate);
                              formHook.clearErrors(`product_info.${index}.expiry_date`);
                            }}
                            defaultSelectedDate={formHook.getValues(`product_info.${index}.expiry_date`)}
                            className="w-full"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <button onClick={() => POProductsHook.remove(index)} className="text-red-500">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {POProductsHook?.fields?.length > 0 && (
            <div className="flex sm:flex-row flex-col sm:items-baseline items-start mt-5">
              <div className="flex justify-start items-center gap-1 p-2.5">
                <span className="font-normal text-black text-xs">Reason</span>
                <span className="font-normal text-rose-600 text-xs">*</span>
              </div>
              <textarea
                rows={4}
                placeholder="Enter your reason here"
                className="sm:w-2/5 lg:w-1/4 w-full p-2 border border-gray-300 rounded-xl text-sm"
                {...formHook.register(`reason`, {
                  required: true,
                  validate: (value) => value.trim() !== "" || "Reason cannot be empty or just spaces",
                })}
              />
            </div>
          )}
          {POProductsHook?.fields?.length > 0 && formHook?.formState?.errors?.reason && (
            <p className="text-red-500 text-[10px] mt-1 sm:ml-20 ml-2.5">Reason is required</p>
          )}
        </div>
        <div className="flex justify-end items-center gap-4 bottom-0 left-0 right-0 fixed bg-white mt-10 border-t-2 w-full p-3">
          <button
            type="submit"
            className="text-sm mt-3 inline-flex w-full justify-center rounded-md bg-[#193A6A] px-10 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddInventory;
